/**
 * Auth Guard
 *
 * Validates JWT and protects authenticated routes
 * Redirects to sign-in if token is invalid or expired
 */

import { useMemo, useEffect } from "react";
import type { ReactNode } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/contexts/AuthContext";
import { validateJWT, isJWTExpired } from "@/lib/auth/jwt";

export function AuthGuard({ children }: { children: ReactNode }) {
  const { token, logout } = useAuth();
  const location = useLocation();

  // Skip validation for /s/ share routes
  const isShareRoute = location.pathname.startsWith("/s/");

  // Validate JWT
  const isValid = useMemo(() => {
    if (isShareRoute) return true; // Skip validation for share routes
    if (!token) return false;
    return validateJWT(token);
  }, [token, isShareRoute]);

  // Auto-logout on invalid/expired token
  useEffect(() => {
    if (!isShareRoute && token && !isValid) {
      console.warn("JWT token is invalid or expired, logging out");
      logout();
    }
  }, [token, isValid, logout, isShareRoute]);

  // Share routes bypass auth
  if (isShareRoute) {
    return <>{children}</>;
  }

  // Redirect to sign-in if not authenticated
  if (!token || !isValid) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
